import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

import QASearchForm, { QASearchParams } from '../components/QALegacySearchForm';
import QAVisitsList from '../components/QALegacyVisitsList';
import QAVisitDetails from '../components/QALegacyVisitDetails';
import {
  searchQAVisits,
  getVisitHnps,
  getVisitData,
  downloadPdf,
} from '../services/rogerDocApi';
import { QAVisit, VisitDataResponse } from '@/types/visitData.types';

const QALegacy: React.FC = () => {
  const { visitId } = useParams<{ visitId?: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  // Search parameters state
  const [searchParams, setSearchParams] = useState<QASearchParams>({
    patient_id: '',
    patient_first_name: '',
    patient_last_name: '',
    start_date: null,
    end_date: null,
  });

  const [searchResults, setSearchResults] = useState<QAVisit[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Selected visit state
  const [selectedVisit, setSelectedVisit] = useState<QAVisit | null>(null);
  const [loadingVisitDetails, setLoadingVisitDetails] = useState(false);
  const [visitHnps, setVisitHnps] = useState<
    Array<{ name?: string; key: string }>
  >([]);
  const [visitData, setVisitData] = useState<VisitDataResponse>();
  const [loadingFromUrl, setLoadingFromUrl] = useState(false);

  // Validation function for search parameters
  const validateSearchParams = (): string[] => {
    const errors: string[] = [];

    if (
      searchParams.patient_first_name.trim() &&
      searchParams.patient_first_name.trim().length < 3
    ) {
      errors.push('First name must be at least 3 characters long');
    }

    if (
      searchParams.patient_last_name.trim() &&
      searchParams.patient_last_name.trim().length < 3
    ) {
      errors.push('Last name must be at least 3 characters long');
    }

    if (searchParams.start_date) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (searchParams.start_date > today) {
        errors.push('Start date cannot be in the future');
      }
    }

    return errors;
  };

  // Helper function to format search parameters for API
  const formatSearchParamsForAPI = () => {
    const params: Record<string, string> = {};

    if (searchParams.patient_id.trim()) {
      params.patient_id = searchParams.patient_id.trim();
    }

    if (searchParams.patient_first_name.trim()) {
      params.patient_first_name = searchParams.patient_first_name.trim();
    }

    if (searchParams.patient_last_name.trim()) {
      params.patient_last_name = searchParams.patient_last_name.trim();
    }

    if (searchParams.start_date) {
      params.start_date = searchParams.start_date.toISOString().split('T')[0];
    }

    if (searchParams.end_date) {
      params.end_date = searchParams.end_date.toISOString().split('T')[0];
    }

    return params;
  };

  // Update search parameter
  const updateSearchParam = (
    key: keyof QASearchParams,
    value: string | Date | null
  ) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: value,
    }));

    // Clear validation error when parameters change
    setValidationError(null);

    // Reset search state when any parameter changes
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleSearchVisits = async () => {
    // Validate search parameters
    const validationErrors = validateSearchParams();
    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join('. '));
      return;
    }

    // Clear validation error if validation passes
    setValidationError(null);

    setSearching(true);
    setSearchResults([]);
    setHasSearched(false);

    try {
      const visits = await searchQAVisits(formatSearchParamsForAPI());
      setSearchResults(visits);
      setHasSearched(true);

      if (visits.length === 0) {
        showError('No visits available with current filters');
      } else {
        showSuccess(`${visits.length} visit(s) available for QA review`);
      }
    } catch (error: unknown) {
      console.error('Error loading QA visits:', error);
      showError('Failed to load visits. Please try again.');
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  // Handle URL-based visit loading and automatic search
  useEffect(() => {
    if (visitId) {
      // Load visit directly from URL parameter
      loadVisitFromUrl(visitId);
    } else {
      // Automatically search on component mount when no visitId
      handleSearchVisits();
    }
  }, [visitId]);

  const handleClearFilters = () => {
    setSearchParams({
      patient_id: '',
      patient_first_name: '',
      patient_last_name: '',
      start_date: null,
      end_date: null,
    });
    setValidationError(null);
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleVisitClick = async (visit: QAVisit) => {
    // Navigate to the visit-specific URL
    navigate(`/qa-legacy/${visit.visit_id}`);

    setSelectedVisit(visit);
    setLoadingVisitDetails(true);
    setVisitHnps([]);
    setVisitData(undefined);

    try {
      // Load HnP PDFs and visit data in parallel
      const [hnpsData, visitDataResponse] = await Promise.all([
        getVisitHnps(visit.visit_id),
        getVisitData(visit.visit_id),
      ]);

      setVisitHnps(Array.isArray(hnpsData) ? hnpsData : []);
      setVisitData(visitDataResponse);

      showSuccess('Visit details loaded successfully');
    } catch (error: unknown) {
      console.error('Error loading visit details:', error);
      showError('Failed to load visit details. Please try again.');
    } finally {
      setLoadingVisitDetails(false);
    }
  };

  const handleBackToList = () => {
    // Navigate back to the main QA page
    navigate('/qa-legacy');

    setSelectedVisit(null);
    setVisitHnps([]);
    setVisitData(undefined);
  };

  // Load visit data directly from URL parameter
  const loadVisitFromUrl = async (visitIdParam: string) => {
    if (!visitIdParam) return;

    setLoadingFromUrl(true);
    setSelectedVisit(null);
    setVisitHnps([]);
    setVisitData(undefined);

    try {
      // Load HnP PDFs and visit data in parallel
      const [hnpsData, visitDataResponse] = await Promise.all([
        getVisitHnps(visitIdParam),
        getVisitData(visitIdParam),
      ]);

      setVisitHnps(Array.isArray(hnpsData) ? hnpsData : []);
      setVisitData(visitDataResponse);

      // Extract visit information from the API response to populate selectedVisit
      const visitFromUrl: QAVisit = {
        visit_id: visitIdParam,
        patient_id: (visitDataResponse?.patient_id as string) || '',
        patient_first_name:
          (visitDataResponse?.patient_first_name as string) || '',
        patient_last_name:
          (visitDataResponse?.patient_last_name as string) || '',
        visit_date: (visitDataResponse?.visit_date as string) || '',
        visit_type: (visitDataResponse?.visit_type as string) || '',
        nurse_username: (visitDataResponse?.nurse_username as string) || '',
      };
      setSelectedVisit(visitFromUrl);

      showSuccess('Visit details loaded successfully');
    } catch (error: unknown) {
      console.error('Error loading visit details from URL:', error);
      showError('Failed to load visit details. Please try again.');
      // Navigate back to QA list on error
      navigate('/qa-legacy');
    } finally {
      setLoadingFromUrl(false);
    }
  };

  const handleOpenPdf = async (pdfKey: string) => {
    try {
      const blob = await downloadPdf(pdfKey);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up the blob URL after opening
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error: unknown) {
      console.error('Failed to open PDF:', error);
      showError('Failed to open PDF. Please try again.');
    }
  };

  const getLatestFormAnswerState = () => {
    // Handle both old (form_answer_states) and new (form_answer_state) API responses
    if (!visitData || typeof visitData !== 'object') return null;

    const formStates =
      'form_answer_states' in visitData && visitData.form_answer_states
        ? visitData.form_answer_states
        : 'form_answer_state' in visitData && visitData.form_answer_state
          ? visitData.form_answer_state
          : null;

    if (!formStates) return null;

    // If it's the new singular form_answer_state, return it directly
    if ('form_answer_state' in visitData && visitData.form_answer_state) {
      return {
        timestamp: new Date().toISOString(), // Use current timestamp if not provided
        data: visitData.form_answer_state,
      };
    }

    // Handle the old plural form_answer_states structure
    if (typeof formStates === 'object' && formStates !== null) {
      const states = Object.entries(formStates);
      if (states.length === 0) return null;

      // Sort by key (timestamp) and get the latest
      states.sort((a, b) => b[0].localeCompare(a[0]));
      return {
        timestamp: states[0][0],
        data: states[0][1],
      };
    }

    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <div className='container mx-auto px-4 py-6'>
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-900'>Quality Assurance</h1>
      </div>

      {/* Check if medical coding/QA is enabled for the agency */}
      {/* TODO: Add agency configuration check for QA feature */}
      {/* For now, assume it's enabled - this check can be added later */}
      {selectedVisit && visitData ? (
        // Visit Details View
        <QAVisitDetails
          visit={selectedVisit}
          visitHnps={visitHnps}
          visitData={visitData}
          loading={loadingVisitDetails || loadingFromUrl}
          onBack={handleBackToList}
          onOpenPdf={handleOpenPdf}
          formatDate={formatDate}
          getLatestFormAnswerState={getLatestFormAnswerState}
        />
      ) : (
        // Visit List View
        <>
          <QASearchForm
            searchParams={searchParams}
            onUpdateSearchParam={updateSearchParam}
            onSearch={handleSearchVisits}
            onClearFilters={handleClearFilters}
            searching={searching}
            validationError={validationError}
            onCloseValidationError={() => setValidationError(null)}
          />

          <QAVisitsList
            searchResults={searchResults}
            searching={searching}
            hasSearched={hasSearched}
            onVisitClick={handleVisitClick}
          />
        </>
      )}
    </div>
  );
};

export default QALegacy;
